import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FamilyInput, DancerInput } from '@pirouette/shared';
import type { TablesInsert } from '@pirouette/db';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';

// Import-batch tracking + commit helpers. The wizard creates a batch row
// first, then commits the validated rows in chunks of 50.

export type ImportKind = 'families' | 'dancers' | 'enrollments' | 'classes' | 'lineup';
export type ImportStatus =
  | 'pending'
  | 'validating'
  | 'previewing'
  | 'committed'
  | 'partial'
  | 'failed'
  | 'cancelled';

export interface ImportBatch {
  id: string;
  created_at: string;
  kind: ImportKind;
  status: ImportStatus;
  source_filename: string | null;
  row_count: number;
  rows_committed: number;
  rows_failed: number;
}

export function useImportBatches() {
  return useQuery({
    queryKey: ['import-batches'],
    queryFn: async (): Promise<ImportBatch[]> => {
      const { data, error } = await supabase
        .from('import_batches')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });
}

const CHUNK_SIZE = 50;

export interface CommitResult {
  committed: number;
  failed: number;
  failedChunks: { offset: number; size: number; message: string }[];
}

function deriveStatus(result: CommitResult): ImportStatus {
  if (result.failed === 0) return 'committed';
  if (result.committed === 0) return 'failed';
  return 'partial';
}

async function commitInChunks<T extends FamilyInput | DancerInput>(
  table: 'families' | 'dancers',
  rows: T[],
): Promise<CommitResult> {
  let committed = 0;
  let failed = 0;
  const failedChunks: CommitResult['failedChunks'] = [];

  for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
    const chunk = rows.slice(i, i + CHUNK_SIZE);
    const payload = chunk.map(stripUndefined);
    // Branch on the table literal so `.from()` resolves to a single concrete
    // table and `.insert()` accepts that table's narrow Insert type. `T` is
    // generic and can't be correlated to `table` by the type system, but the
    // two call sites pair them correctly (families rows -> families table).
    const { error, count } =
      table === 'families'
        ? await supabase
            .from('families')
            .insert(payload as TablesInsert<'families'>[], { count: 'exact' })
        : await supabase
            .from('dancers')
            .insert(payload as TablesInsert<'dancers'>[], { count: 'exact' });
    if (error) {
      failed += chunk.length;
      failedChunks.push({ offset: i, size: chunk.length, message: error.message });
    } else {
      committed += count ?? chunk.length;
    }
  }

  return { committed, failed, failedChunks };
}

export function useCommitFamilyImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { batchId: string; rows: FamilyInput[] }): Promise<CommitResult> => {
      const { batchId, rows } = args;
      await supabase.from('import_batches').update({ status: 'validating' }).eq('id', batchId);
      const result = await commitInChunks('families', rows);
      await supabase
        .from('import_batches')
        .update({
          status: deriveStatus(result),
          rows_committed: result.committed,
          rows_failed: result.failed,
          error_report:
            result.failedChunks.length > 0 ? { failedChunks: result.failedChunks } : null,
        })
        .eq('id', batchId);
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['families'] });
      void qc.invalidateQueries({ queryKey: ['import-batches'] });
    },
  });
}

export function useCommitDancerImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { batchId: string; rows: DancerInput[] }): Promise<CommitResult> => {
      const { batchId, rows } = args;
      await supabase.from('import_batches').update({ status: 'validating' }).eq('id', batchId);
      const result = await commitInChunks('dancers', rows);
      await supabase
        .from('import_batches')
        .update({
          status: deriveStatus(result),
          rows_committed: result.committed,
          rows_failed: result.failed,
          error_report:
            result.failedChunks.length > 0 ? { failedChunks: result.failedChunks } : null,
        })
        .eq('id', batchId);
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dancers'] });
      void qc.invalidateQueries({ queryKey: ['import-batches'] });
    },
  });
}

export function useCreateImportBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      kind: ImportKind;
      filename: string;
      rowCount: number;
    }): Promise<ImportBatch> => {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (!userId) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('import_batches')
        .insert({
          kind: args.kind,
          status: 'pending',
          source_filename: args.filename,
          row_count: args.rowCount,
          initiated_by: userId,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['import-batches'] });
    },
  });
}

// Bounded collision check — sends the candidate emails and gets back only
// the subset that already exists. Avoids streaming a full family roster
// to the browser (was unbounded; would have timed out on large studios).
export async function fetchExistingFamilyEmails(candidates: string[]): Promise<Set<string>> {
  const lower = [...new Set(candidates.map((e) => e.toLowerCase()))];
  if (lower.length === 0) return new Set();
  // chunk to avoid an extremely large IN list — 500 emails per round-trip.
  const CHUNK = 500;
  const matched = new Set<string>();
  for (let i = 0; i < lower.length; i += CHUNK) {
    const slice = lower.slice(i, i + CHUNK);
    const { data, error } = await supabase.rpc('find_existing_family_emails', {
      p_emails: slice,
    });
    if (error) throw error;
    for (const row of (data ?? []) as { email: string }[]) {
      matched.add(row.email.toLowerCase());
    }
  }
  return matched;
}

export async function fetchFamilyIdsByEmail(emails: string[]): Promise<Map<string, string>> {
  const lower = [...new Set(emails.map((e) => e.toLowerCase()))];
  if (lower.length === 0) return new Map();
  const { data, error } = await supabase
    .from('families')
    .select('id, primary_guardian_email')
    .is('deleted_at', null)
    .in('primary_guardian_email', lower);
  if (error) throw error;
  const map = new Map<string, string>();
  for (const r of data ?? []) {
    const row = r;
    map.set(row.primary_guardian_email.toLowerCase(), row.id);
  }
  return map;
}
